import React, { useMemo, useRef } from 'react'
import type { CSSProperties } from 'react'
import type { AreaChartPoint } from '../AreaChart/AreaChart.types'
import type { SmoothAreaChartProps } from './SmoothAreaChart.types'
import {
  SMOOTH_AREA_CHART_DEFAULT_DATA,
  SMOOTH_AREA_CHART_DEFAULT_HEIGHT,
  SMOOTH_AREA_CHART_DEFAULT_STROKE_WIDTH,
  SMOOTH_AREA_CHART_DEFAULT_WIDTH,
  SMOOTH_AREA_CHART_FILL_COLOR,
  SMOOTH_AREA_CHART_FILL_OPACITY,
  SMOOTH_AREA_CHART_STROKE_COLOR,
} from './SmoothAreaChart.types'
import { ChartTooltip, useSeriesChartTooltip } from '../ChartTooltip'
import './SmoothAreaChart.css'

type PlotPoint = { x: number; y: number }

const resolveData = (data: AreaChartPoint[]): AreaChartPoint[] => {
  const usable = data.filter((point) => Number.isFinite(point.value))
  return usable.length > 0 ? usable : SMOOTH_AREA_CHART_DEFAULT_DATA
}

const buildPlotPoints = (
  data: AreaChartPoint[],
  width: number,
  height: number,
  minValue: number,
  maxValue: number,
  insetY: number,
): PlotPoint[] => {
  const range = Math.max(maxValue - minValue, Number.EPSILON)
  const lastIndex = Math.max(data.length - 1, 1)
  const plotHeight = Math.max(height - insetY * 2, 0)

  return data.map((point, index) => {
    // Span full plot width so first/last points meet the grid edges.
    const x = data.length === 1 ? 0 : (index / lastIndex) * width
    const normalized = (point.value - minValue) / range
    const y = insetY + (1 - normalized) * plotHeight
    return { x, y }
  })
}

/**
 * Monotone cubic Hermite (Fritsch–Carlson) → cubic Bézier path.
 * Preserves local shape without overshooting peaks (avoids clipped flat tops).
 */
const buildMonotoneLinePath = (points: PlotPoint[]): string => {
  if (points.length === 0) return ''
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`
  if (points.length === 2) {
    return `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y}`
  }

  const n = points.length
  const dx: number[] = []
  const dy: number[] = []
  const secant: number[] = []

  for (let i = 0; i < n - 1; i += 1) {
    dx[i] = points[i + 1].x - points[i].x
    dy[i] = points[i + 1].y - points[i].y
    secant[i] = dy[i] / Math.max(dx[i], Number.EPSILON)
  }

  const slopes: number[] = new Array(n)
  slopes[0] = secant[0]
  slopes[n - 1] = secant[n - 2]

  for (let i = 1; i < n - 1; i += 1) {
    if (secant[i - 1] * secant[i] <= 0) {
      slopes[i] = 0
    } else {
      slopes[i] = (secant[i - 1] + secant[i]) / 2
    }
  }

  for (let i = 0; i < n - 1; i += 1) {
    if (Math.abs(secant[i]) < 1e-12) {
      slopes[i] = 0
      slopes[i + 1] = 0
      continue
    }

    const a = slopes[i] / secant[i]
    const b = slopes[i + 1] / secant[i]
    const s = a * a + b * b
    if (s > 9) {
      const t = 3 / Math.sqrt(s)
      slopes[i] = t * a * secant[i]
      slopes[i + 1] = t * b * secant[i]
    }
  }

  let path = `M ${points[0].x} ${points[0].y}`

  for (let i = 0; i < n - 1; i += 1) {
    const p0 = points[i]
    const p1 = points[i + 1]
    const cp1x = p0.x + dx[i] / 3
    const cp1y = p0.y + (slopes[i] * dx[i]) / 3
    const cp2x = p1.x - dx[i] / 3
    const cp2y = p1.y - (slopes[i + 1] * dx[i]) / 3
    path += ` C ${cp1x} ${cp1y} ${cp2x} ${cp2y} ${p1.x} ${p1.y}`
  }

  return path
}

const buildMonotoneAreaPath = (
  points: PlotPoint[],
  width: number,
  height: number,
  insetY: number,
): string => {
  if (points.length === 0) return ''
  const line = buildMonotoneLinePath(points)
  const bottom = height - insetY
  return `${line} L ${width} ${bottom} L 0 ${bottom} Z`
}

const SmoothAreaChart: React.FC<SmoothAreaChartProps> = ({
  data = SMOOTH_AREA_CHART_DEFAULT_DATA,
  width = SMOOTH_AREA_CHART_DEFAULT_WIDTH,
  height = SMOOTH_AREA_CHART_DEFAULT_HEIGHT,
  strokeColor = SMOOTH_AREA_CHART_STROKE_COLOR,
  fillColor = SMOOTH_AREA_CHART_FILL_COLOR,
  fillOpacity = SMOOTH_AREA_CHART_FILL_OPACITY,
  strokeWidth = SMOOTH_AREA_CHART_DEFAULT_STROKE_WIDTH,
  maxValue,
  minValue = 0,
  showTooltip = true,
  seriesName = 'Value',
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const resolvedData = useMemo(() => resolveData(data), [data])
  /** Vertical inset only — keep stroke clear of top/bottom while spanning full width. */
  const insetY = Math.max(strokeWidth / 2 + 1, 2)
  const format = formatValue ?? ((value: number) => String(value))

  const resolvedMax = useMemo(() => {
    if (maxValue != null && Number.isFinite(maxValue)) return maxValue
    const peak = Math.max(...resolvedData.map((point) => point.value))
    return peak > minValue ? peak : minValue + 1
  }, [maxValue, minValue, resolvedData])

  const plotPoints = useMemo(
    () =>
      buildPlotPoints(
        resolvedData,
        width,
        height,
        minValue,
        resolvedMax,
        insetY,
      ),
    [height, insetY, minValue, resolvedData, resolvedMax, width],
  )

  const linePath = useMemo(
    () => buildMonotoneLinePath(plotPoints),
    [plotPoints],
  )
  const areaPath = useMemo(
    () => buildMonotoneAreaPath(plotPoints, width, height, insetY),
    [height, insetY, plotPoints, width],
  )

  const { tooltip, onMouseMove, onMouseLeave } = useSeriesChartTooltip({
    enabled: showTooltip,
    containerRef,
    plotPoints,
    viewWidth: width,
    viewHeight: height,
  })

  const activePoint =
    tooltip.visible && tooltip.index >= 0 ? resolvedData[tooltip.index] : null

  const classNames = [
    'ds-smooth-area-chart',
    showTooltip ? 'ds-smooth-area-chart--interactive' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-smooth-area-chart-height': `${height}px`,
  } as CSSProperties

  return (
    <div
      ref={containerRef}
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? 'Smooth area chart'}
      onMouseMove={showTooltip ? onMouseMove : undefined}
      onMouseLeave={showTooltip ? onMouseLeave : undefined}
    >
      <svg
        className="ds-smooth-area-chart__svg"
        viewBox={`0 0 ${width} ${height}`}
        width="100%"
        height="100%"
        preserveAspectRatio="none"
        aria-hidden="true"
        overflow="visible"
      >
        <path
          d={areaPath}
          fill={fillColor}
          fillOpacity={fillOpacity}
          stroke="none"
        />
        <path
          d={linePath}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      {showTooltip && tooltip.visible ? (
        <div
          className={`ds-chart-tooltip-layer${
            darkMode ? ' ds-chart-tooltip-layer--dark' : ''
          }`}
          aria-hidden="true"
        >
          <span
            className="ds-chart-tooltip-crosshair"
            style={{ left: tooltip.pointX }}
          />
          <span
            className="ds-chart-tooltip-marker"
            style={{
              left: tooltip.pointX,
              top: tooltip.pointY,
              background: strokeColor,
            }}
          />
        </div>
      ) : null}

      {showTooltip && activePoint ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={activePoint.label ?? `Point ${tooltip.index + 1}`}
          x={tooltip.pointX}
          y={tooltip.pointY}
          items={[
            {
              label: seriesName,
              value: format(activePoint.value),
              color: strokeColor,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default SmoothAreaChart
